import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EmployerService } from '@/services/employer.service';
import { PreFundingStatus } from '@/models/employer.model';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { Building2, ArrowLeft, Trash2, Users, DollarSign, CheckCircle2, XCircle, Calendar, CreditCard, Shield, FileText } from 'lucide-react';

export const EmployerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: employer, isLoading } = useQuery({
    queryKey: ['employer', id],
    queryFn: () => (id ? EmployerService.getEmployerById(id, true) : null),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await EmployerService.deleteEmployer(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employers'] });
      navigate('/employers');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async () => {
      if (!id || !employer) return;
      await EmployerService.toggleEmployerStatus(id, employer.isActive);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employer', id] });
      queryClient.invalidateQueries({ queryKey: ['employers'] });
    },
  });

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return format(date, 'MMMM d, yyyy');
  };

  const getPreFundingStatusLabel = (status: PreFundingStatus) => {
    switch (status) {
      case PreFundingStatus.monthly:
        return 'Monthly';
      case PreFundingStatus.annual:
        return 'Annual';
      default:
        return 'None';
    }
  };

  const [activeTab, setActiveTab] = useState<'overview' | 'payment' | 'members' | 'compliance'>('overview');

  if (isLoading) {
    return <LoadingSpinner text="Loading employer details..." fullScreen />;
  }

  if (!employer) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Building2}
          title="Employer not found"
          description="The requested employer does not exist."
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/employers')}>
              Back to Employers
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/employers')}
          className="mb-4"
        >
          Back to Employers
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{employer.name}</h1>
                  <Badge variant={employer.isActive ? "success" : "gray"} size="md">
                    {employer.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">Employer Details</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={employer.isActive ? "danger" : "success"}
                size="sm"
                onClick={() => toggleStatusMutation.mutate()}
                isLoading={toggleStatusMutation.isPending}
              >
                {employer.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={Trash2}
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this employer?')) {
                    deleteMutation.mutate();
                  }
                }}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Card padding="none">
        <div className="border-b border-gray-200 bg-gray-50/50">
          <nav className="flex -mb-px px-6">
            {[
              { id: 'overview', label: 'Overview', icon: FileText },
              { id: 'payment', label: 'Payment & Billing', icon: CreditCard },
              { id: 'members', label: 'Members', icon: Users },
              { id: 'compliance', label: 'Compliance', icon: Shield },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="text-sm font-medium text-blue-600 mb-1">Total Members</div>
                  <div className="text-3xl font-bold text-blue-900">{employer.totalMembers}</div>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100">
                  <div className="text-sm font-medium text-green-600 mb-1">Admin Users</div>
                  <div className="text-3xl font-bold text-green-900">{employer.adminUserIds.length}</div>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                  <div className="text-sm font-medium text-purple-600 mb-1">Benefit Plans</div>
                  <div className="text-3xl font-bold text-purple-900">{employer.benefitPlanIds.length}</div>
                </Card>
              </div>

              {employer.description && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{employer.description}</p>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Metadata
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="text-sm text-gray-900">{formatDate(employer.createdAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="text-sm text-gray-900">{formatDate(employer.lastUpdated)}</dd>
                    </div>
                  </dl>
                </Card>

                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Status
                  </h3>
                  <dl className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <Badge variant={employer.isActive ? "success" : "gray"} size="sm">
                        {employer.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <dt className="text-sm font-medium text-gray-500">Payment Verified</dt>
                      <Badge variant={employer.paymentMethodVerified ? "success" : "gray"} size="sm">
                        {employer.paymentMethodVerified ? (
                          <>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Verified
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3 mr-1" />
                            Not Verified
                          </>
                        )}
                      </Badge>
                    </div>
                  </dl>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    Payment Information
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Stripe Customer ID</dt>
                      <dd className="text-sm text-gray-900 font-mono">{employer.stripeCustomerId || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Payment Method ID</dt>
                      <dd className="text-sm text-gray-900 font-mono">{employer.paymentMethodId || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Payment Method Verified</dt>
                      <dd className="text-sm text-gray-900">
                        <Badge variant={employer.paymentMethodVerified ? "success" : "gray"} size="sm">
                          {employer.paymentMethodVerified ? 'Yes' : 'No'}
                        </Badge>
                      </dd>
                    </div>
                    {employer.paymentMethodAddedAt && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Payment Method Added</dt>
                        <dd className="text-sm text-gray-900">{formatDate(employer.paymentMethodAddedAt)}</dd>
                      </div>
                    )}
                    {employer.monthlyBudgetLimit && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Monthly Budget Limit</dt>
                        <dd className="text-sm font-semibold text-gray-900">{formatCurrency(employer.monthlyBudgetLimit)}</dd>
                      </div>
                    )}
                  </dl>
                </Card>

                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    Pre-Funding
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Pre-Funding Status</dt>
                      <dd className="text-sm text-gray-900">
                        <Badge variant={employer.preFundingStatus !== PreFundingStatus.none ? "info" : "gray"} size="sm">
                          {getPreFundingStatusLabel(employer.preFundingStatus)}
                        </Badge>
                      </dd>
                    </div>
                    {employer.preFundedBalance !== undefined && employer.preFundedBalance !== null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Pre-Funded Balance</dt>
                        <dd className="text-sm font-semibold text-gray-900">{formatCurrency(employer.preFundedBalance)}</dd>
                      </div>
                    )}
                    {employer.preFundingExpiresAt && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Pre-Funding Expires</dt>
                        <dd className="text-sm text-gray-900">{formatDate(employer.preFundingExpiresAt)}</dd>
                      </div>
                    )}
                    {employer.preFundingDiscountPercent > 0 && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Discount Percentage</dt>
                        <dd className="text-sm text-gray-900">{employer.preFundingDiscountPercent}%</dd>
                      </div>
                    )}
                  </dl>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Member Information
              </h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Members</dt>
                  <dd className="text-2xl font-bold text-gray-900">{employer.totalMembers}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Admin Users</dt>
                  <dd className="text-sm text-gray-900">
                    {employer.adminUserIds.length > 0 ? (
                      <div className="space-y-2">
                        {employer.adminUserIds.map((adminId) => (
                          <div key={adminId} className="text-sm font-mono text-gray-700 bg-gray-50 p-2 rounded">
                            {adminId}
                          </div>
                        ))}
                      </div>
                    ) : (
                      'No admin users'
                    )}
                  </dd>
                </div>
              </dl>
            </Card>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-6">
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Compliance Information
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">COBRA Coverage</dt>
                    <dd className="text-sm text-gray-900">
                      <Badge variant={employer.hasCobraCoverage ? "success" : "gray"} size="sm">
                        {employer.hasCobraCoverage ? 'Yes' : 'No'}
                      </Badge>
                    </dd>
                  </div>
                  {employer.stopLossPolicyId && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Stop Loss Policy ID</dt>
                      <dd className="text-sm text-gray-900 font-mono">{employer.stopLossPolicyId}</dd>
                    </div>
                  )}
                  {employer.spdConfigurationId && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">SPD Configuration ID</dt>
                      <dd className="text-sm text-gray-900 font-mono">{employer.spdConfigurationId}</dd>
                    </div>
                  )}
                  {employer.lastComplianceCheck && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Compliance Check</dt>
                      <dd className="text-sm text-gray-900">{formatDate(employer.lastComplianceCheck)}</dd>
                    </div>
                  )}
                </dl>
              </Card>

              {employer.benefitPlanIds.length > 0 && (
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefit Plans</h3>
                  <div className="space-y-2">
                    {employer.benefitPlanIds.map((planId) => (
                      <div key={planId} className="text-sm font-mono text-gray-700 bg-gray-50 p-2 rounded">
                        {planId}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
